import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { KontentStringValue } from '../../../types/KontentProps';
//@ts-ignore
import * as styles from './image-gallery.module.css';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';

type Image = {
    elements: {
        allow_download?: {
            value: any;
        };
        caption?: {
            value: KontentStringValue;
        };
        image?: {
            value: any[];
        };
        file?: {
            value: any[];
        };
    };
};

interface Props {
    headline?: KontentStringValue;
    cta_label?: KontentStringValue;
    images: {
        value: Image[];
    };
    kontentItemId?: string;
}

const ImageGallery: React.FC<Props> = ({
    headline,
    cta_label,
    images,
    kontentItemId,
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showModal, setShowModal] = useState(false);

    const galleryImages = images?.value || [];

    // Show max 5 images on page, rest only in lightbox
    const displayImages = galleryImages.slice(0, 5);
    const allImages = galleryImages; // All images available in lightbox

    const openLightbox = (index: number = 0) => {
        setCurrentImageIndex(index);
        setShowModal(true);
    };

    const closeLightbox = () => {
        setShowModal(false);
    };

    // Handle body scroll lock when modal is open
    useEffect(() => {
        if (showModal) {
            // Save the original overflow value
            const originalOverflow = document.body.style.overflow;
            // Lock body scroll
            document.body.style.overflow = 'hidden';

            // Cleanup: restore original overflow value when modal closes or component unmounts
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [showModal]);

    const goToPrevious = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) =>
            prev === 0 ? allImages.length - 1 : prev - 1
        );
    };

    const goToNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) =>
            prev === allImages.length - 1 ? 0 : prev + 1
        );
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!showModal) return;

            if (e.key === 'Escape') {
                closeLightbox();
            } else if (e.key === 'ArrowLeft') {
                setCurrentImageIndex((prev) =>
                    prev === 0 ? allImages.length - 1 : prev - 1
                );
            } else if (e.key === 'ArrowRight') {
                setCurrentImageIndex((prev) =>
                    prev === allImages.length - 1 ? 0 : prev + 1
                );
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showModal, allImages.length]);

    const getCurrentImage = () => {
        return allImages[currentImageIndex];
    };

    // Helper function to extract image URL from either 'image' or 'file' field
    // Gallery images use 'image', regular images use 'file'
    const getImageUrl = (image: Image | undefined): string | null => {
        if (!image) return null;

        const imageValue = image?.elements?.image?.value;
        const fileValue = image?.elements?.file?.value;

        if (Array.isArray(imageValue) && imageValue.length > 0) {
            return imageValue[0]?.url || null;
        } else if (Array.isArray(fileValue) && fileValue.length > 0) {
            return fileValue[0]?.url || null;
        }
        return null;
    };

    const getCurrentImageUrl = () => {
        return getImageUrl(getCurrentImage()) || '';
    };

    const getCurrentImageCaption = () => {
        const currentImage = getCurrentImage();
        const caption = currentImage?.elements?.caption?.value;
        return (typeof caption === 'string' ? caption : caption?.value) || '';
    };

    const renderImage = (image: Image, idx: number) => {
        const imageUrl = getImageUrl(image);

        if (!imageUrl) return null;

        // Find the index in all images array by comparing image URLs instead of object reference
        const allImagesIndex = allImages.findIndex(
            (img) => getImageUrl(img) === imageUrl
        );
        const captionValue = image?.elements?.caption?.value;
        const caption =
            typeof captionValue === 'string'
                ? captionValue
                : captionValue?.value || '';

        return (
            <div
                className={styles.galleryImage}
                key={idx}
                onClick={() =>
                    openLightbox(allImagesIndex >= 0 ? allImagesIndex : idx)
                }
            >
                <div
                    className={styles.imageWrapper}
                    data-kontent-element-codename="image"
                >
                    <img
                        src={optimizeKontentImageUrl(imageUrl, {
                            ...imagePreset.card.desktop,
                            q: 95,
                        })}
                        srcSet={buildKontentSrcSet(
                            imageUrl,
                            imagePreset.card.desktop.w,
                            {
                                fm: 'webp' as const,
                                q: 95,
                            }
                        )}
                        sizes="(max-width: 576px) 100vw, (max-width: 991px) 50vw, 33vw"
                        alt={caption || `Gallery image ${idx + 1}`}
                        loading="lazy"
                        decoding="async"
                    />
                    <div className={styles.imageOverlay}></div>
                </div>
            </div>
        );
    };

    return (
        <>
            <section
                className={styles.wrapper}
                data-kontent-item-id={kontentItemId}
            >
                <div className={styles.container}>
                    {headline?.value && (
                        <h2
                            className={styles.headline}
                            data-kontent-element-codename="headline"
                        >
                            {headline.value}
                        </h2>
                    )}

                    {allImages.length > 0 && (
                        <div className={styles.ctaContainer}>
                            <button
                                className={styles.viewGalleryButton}
                                onClick={() => openLightbox(0)}
                            >
                                {cta_label?.value || 'View Gallery'}
                            </button>
                        </div>
                    )}

                    <div className={styles.galleryGrid}>
                        {displayImages.map((image: Image, idx: number) =>
                            renderImage(image, idx)
                        )}
                    </div>
                </div>
            </section>

            {showModal && allImages.length > 0 && (
                <div className={styles.background} onClick={closeLightbox}>
                    <div
                        className={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className={styles.closeButton}
                            onClick={closeLightbox}
                            aria-label="Close gallery"
                        >
                            ×
                        </button>

                        <div className={styles.modalContent}>
                            <div className={styles.imageContainer}>
                                {allImages.length > 1 && (
                                    <>
                                        <button
                                            className={`${styles.navButton} ${styles.prevButton}`}
                                            onClick={goToPrevious}
                                            aria-label="Previous image"
                                        >
                                            <span className={styles.chevron}>
                                                ‹
                                            </span>
                                        </button>
                                        <button
                                            className={`${styles.navButton} ${styles.nextButton}`}
                                            onClick={goToNext}
                                            aria-label="Next image"
                                        >
                                            <span className={styles.chevron}>
                                                ›
                                            </span>
                                        </button>
                                    </>
                                )}

                                <div data-kontent-element-codename="image">
                                    <img
                                        src={optimizeKontentImageUrl(
                                            getCurrentImageUrl(),
                                            {
                                                ...imagePreset.fullWidth
                                                    .desktop,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            getCurrentImageUrl(),
                                            imagePreset.fullWidth.desktop.w,
                                            {
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        sizes="100vw"
                                        alt={
                                            getCurrentImageCaption() ||
                                            `Gallery image ${
                                                currentImageIndex + 1
                                            }`
                                        }
                                        className={styles.modalImage}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>

                                {allImages.length > 1 && (
                                    <div className={styles.imageCounter}>
                                        {currentImageIndex + 1} /{' '}
                                        {allImages.length}
                                    </div>
                                )}
                            </div>

                            {getCurrentImageCaption() && (
                                <div className={styles.modalCaption}>
                                    {getCurrentImageCaption()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ImageGallery;

export const fragmentImageGallery = graphql`
    fragment ImageGallery on kontent_item_component___basic_image_gallery {
        system {
            id
            type
        }
        elements {
            headline {
                value
            }
            images {
                value {
                    ... on kontent_item_media___gallery_image {
                        id
                        elements {
                            allow_download {
                                value {
                                    codename
                                }
                            }
                            caption {
                                value
                            }
                            image {
                                value {
                                    url
                                    description
                                    name
                                }
                            }
                        }
                    }
                    ... on kontent_item_media___image {
                        id
                        elements {
                            file {
                                value {
                                    url
                                    description
                                    name
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
